// Writing a post: all of it, once.
//
// This is the whole of what the composer *does* — media in, targets ticked,
// validated, published or scheduled — with nothing about how it looks. It was
// the top two thirds of `pages/Compose.tsx`, and it moved here the day the
// dashboard needed a composer of its own: the alternative was a second
// implementation of upload-validate-create-dispatch that would have drifted
// from this one by the first bug fix.
//
// So there is one pipeline and two views over it. The full screen shows every
// control; the dashboard card shows the common path and links to the screen for
// the rest. Neither of them knows how a carousel is uploaded.
//
// Nothing here is a permission check that matters. `create_post` and its
// triggers refuse independently — role, plan and quota — so every `can…` below
// is a hint that stops somebody committing to something the database will
// reject, and a stale one can only ever be stricter than the real answer.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  createPost, getMedia, listBrandDetails, saveDraft, thumbPath, thumbTarget,
  signMedia, uploadMedia, validateMediaItems, youtubeDeliveriesFor,
} from "../lib/api";
import { missingSentence, readPostDraft, readWhatsAppDraft, readWhatsAppImage, whatsAppDraftPostId } from "../lib/whatsappDraft";
import { callFunction } from "../lib/supabase";
import { convertImage, formatBytes, inspect, type MediaFacts } from "../lib/media";
import { logError, newRunId } from "../lib/logger";
import {
  can, isPaywall, paywallMessage, remaining, schedulingHorizon,
} from "../lib/entitlements";
import { formatInZone, instantToWallClock, wallClockToInstant, zoneName } from "../lib/timezone";
import { useZone } from "../lib/userSettings";
import { forgetEntitlements, useEntitlements } from "../lib/useEntitlements";
import { useConnections } from "../lib/useConnections";
import { useBrands } from "../lib/useBrands";
import type { Brand } from "../lib/brands";
import { useWorkspace } from "../lib/WorkspaceContext";
import { uploadToYouTube } from "../lib/youtubeUpload";
import { CAPTION_TEMPLATES } from "../lib/captionTemplates";
import { effectiveDetails, resolveTokens, tokensIn } from "../lib/tokens";
import type {
  BrandDetail, Connection, MediaAsset, Platform, PostKind, ValidationResult, YouTubePrivacy,
} from "../lib/types";
import { MAX_PHOTOS, nextKey, releasePreview, type ComposeItem, type Stage } from "./types";
import { mergeVerdicts } from "./verdicts";

export interface Composer {
  // ---- what is being written
  whatsappRequestId: string | null;
  /** A WhatsApp draft is open: by old request link or by draft post id. */
  whatsappFlow: boolean;
  whatsappReady: boolean;
  items: ComposeItem[];
  caption: string;
  title: string;
  /** A wall clock, not an instant. It means nothing without `zone`. */
  scheduledAt: string;
  ytPrivacy: YouTubePrivacy;
  selected: Set<string>;

  // ---- what that means
  workspaceId: string | null;
  facts: MediaFacts | null;
  kind: PostKind | null;
  isVideo: boolean;
  slotsLeft: number;
  canAddMore: boolean;
  activeConnections: Connection[];
  /** The workspace's brands, so the picker can group by drawer (ADR-0029). */
  brands: Brand[];
  /** The brand chosen in the top bar. Drawn first, and preselected once. */
  activeBrandId: string | null;
  /** Change it from here. The same choice the top bar holds, not a second one:
   *  "which brand am I working in" must have one answer per session. */
  selectBrand: (brandId: string | null) => void;
  selectedPlatforms: Platform[];
  results: Map<string, ValidationResult>;
  checking: boolean;
  allReady: boolean;
  unresolved: string[];
  detailValues: Map<string, string>;
  details: BrandDetail[];

  // ---- what the plan and the role allow (hints, never the gate)
  canVideo: boolean;
  canSchedule: boolean;
  horizon: Date | null;
  horizonInputMax: string | undefined;
  /** The IANA zone `scheduledAt` is written in, and every date here read in. */
  zone: string;
  /** That zone said out loud — `India Standard Time` — for the field's label. */
  zoneName: string;
  postsLeft: number | null;
  videoBlocked: boolean;
  mayUpload: boolean;
  blockedByRole: boolean;
  entSettled: boolean;

  // ---- what is happening
  stage: Stage;
  busy: boolean;
  progress: number;
  uploaded: number;
  error: string | null;
  notice: string | null;
  paywall: string | null;
  savingDraft: boolean;

  // ---- doing something about it
  setCaption: (text: string) => void;
  setTitle: (text: string) => void;
  setScheduledAt: (value: string) => void;
  setYtPrivacy: (value: YouTubePrivacy) => void;
  setError: (message: string | null) => void;
  setNotice: (message: string | null) => void;
  addFiles: (files: File[]) => Promise<void>;
  useAssets: (assets: MediaAsset[], urls: Map<string, string>) => void;
  removeItem: (key: string) => void;
  moveItem: (key: string, by: -1 | 1) => void;
  toggleTarget: (connectionId: string) => void;
  /** Tick or untick a whole brand at once. */
  setTargets: (connectionIds: string[], on: boolean) => void;
  applyFix: (platform: string, fix: string) => Promise<void>;
  applyTemplate: (key: string) => void;
  replaceCaption: (text: string, from: "template" | "draft") => void;
  fillTokens: () => void;
  captionUndo: { text: string; from: "template" | "draft" } | null;
  undoCaption: () => void;
  saveAsDraft: () => Promise<void>;
  publish: () => Promise<void>;
  /** Everything back to empty, after the dashboard publishes without leaving. */
  reset: () => void;
}

export function useComposer(): Composer {
  // Creating a draft, scheduling it and publishing it are separate authorities
  // (ADR-0011). The database refuses independently; this only decides what the
  // button says.
  const { can: canDo, active, select: selectWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();

  // The workspace comes from the switcher rather than from a query of its own.
  // Before this it was re-derived here, which meant changing workspace left the
  // composer pointed at the previous one until the page was reloaded.
  const workspaceId = active?.workspace_id ?? null;

  const { connections } = useConnections(workspaceId);
  // Which drawers the accounts are in, and which one the top bar is showing.
  // Read here rather than passed in, so both composers — the full page and the
  // dashboard's quick card — get it without either one plumbing it through.
  const { brands, ready: brandsReady, activeBrandId, selectBrand } = useBrands(workspaceId);
  const { entitlements: ent, settled: entSettled } = useEntitlements(workspaceId);

  // The brand's repeated facts — booking link, phone. Read here so a caption
  // picked from the library arrives finished rather than full of {{braces}}.
  //
  // Every row in the workspace: both the ones filed under a brand and the ones
  // shared by all of them (20260918000300). Which of the two applies is decided
  // below, against the brand on screen.
  const [allDetails, setAllDetails] = useState<BrandDetail[]>([]);
  // Set when the database refuses on plan grounds, so the message can carry an
  // upgrade link rather than reading like a failure.
  const [paywall, setPaywall] = useState<string | null>(null);

  // The post's media, in the order it will be seen. One item is an ordinary
  // post; two to ten photos is an Instagram carousel and a Facebook multi-photo
  // post, which are the same post made twice by two different endpoints.
  const [items, setItems] = useState<ComposeItem[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<Map<string, ValidationResult>>(new Map());
  // Kept beside the merged view because a fix has to know WHICH photo to
  // convert, and the merged verdict has deliberately thrown that away.
  const [itemResults, setItemResults] = useState<Map<string, ValidationResult[]>>(new Map());
  const [checking, setChecking] = useState(false);

  const [caption, setCaptionState] = useState("");
  // What a template or a saved draft overwrote, and which of the two did it.
  const [captionUndo, setCaptionUndo] =
    useState<{ text: string; from: "template" | "draft" } | null>(null);
  const [title, setTitle] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  // The clock `scheduledAt` is written on. From the person's profile, falling
  // back to this device — never read off the browser directly, or the two
  // composers could disagree on what the same digits mean.
  const zone = useZone();
  // YouTube's Required Minimum Functionality: the publisher chooses this, we
  // never decide it for them. Shown before publishing, never changed after.
  const [ytPrivacy, setYtPrivacy] = useState<YouTubePrivacy>("public");

  const [stage, setStage] = useState<Stage>("idle");
  const [progress, setProgress] = useState(0);
  // How many files are already in storage, so a carousel says "3 of 6" instead
  // of sitting on one sentence for a minute.
  const [uploaded, setUploaded] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);

  const whatsappRequestId = params.get("whatsapp");
  // The canonical draft post (20260924000600): the fix link is /compose?post=<id>.
  const draftPostId = params.get("post");
  const whatsappFlow = !!(whatsappRequestId || draftPostId);
  const [draftWorkspaceId, setDraftWorkspaceId] = useState<string | null>(null);
  const [whatsappRevision, setWhatsappRevision] = useState<number | null>(null);
  const [whatsappReady, setWhatsappReady] = useState(false);
  const initializedBrand = useRef<string | null>(null);
  useEffect(() => {
    if (!whatsappFlow || !workspaceId || !brandsReady ||
        brands.some((brand) => brand.workspace_id !== workspaceId) || initializedBrand.current === workspaceId) return;
    initializedBrand.current = workspaceId;
    selectBrand(brands.length === 1 ? brands[0].id : null);
    setSelected(new Set());
  }, [whatsappFlow, workspaceId, brandsReady, brands, selectBrand]);

  // The draft lives in one workspace; open it there.
  useEffect(() => {
    if (draftWorkspaceId && workspaceId && draftWorkspaceId !== workspaceId) selectWorkspace(draftWorkspaceId);
  }, [draftWorkspaceId, workspaceId, selectWorkspace]);

  useEffect(() => {
    if (!draftPostId || whatsappRequestId) return;
    let cancelled = false;
    setWhatsappReady(false);
    setCaptionState("");
    setItems((previous) => { previous.forEach(releasePreview); return []; });
    setNotice("Loading your draft…");
    void (async () => {
      try {
        const draft = await readPostDraft(draftPostId);
        if (cancelled) return;
        if (draft.submitted) { navigate(`/posts/${draft.id}`, { replace: true }); return; }
        setDraftWorkspaceId(draft.workspace_id);
        setCaptionState(draft.caption);
        if (draft.whatsapp_request_id && draft.has_whatsapp_media) {
          try {
            const file = await readWhatsAppImage(draft.whatsapp_request_id);
            const facts = await inspect(file);
            if (cancelled) return;
            const item = { key: nextKey(), file, asset: null, facts, previewUrl: URL.createObjectURL(file) };
            setItems((previous) => { previous.forEach(releasePreview); return [item]; });
          } catch (e) {
            if (cancelled) return;
            setError(e instanceof Error ? e.message : "Upload the WhatsApp photo here.");
          }
        }
        if (cancelled) return;
        setWhatsappReady(true);
        setNotice(`Finish your WhatsApp draft. ${missingSentence(draft.missing)} Choose one brand, then publish or schedule using the controls below.`.replace("  ", " "));
      } catch (e) {
        if (!cancelled) { setNotice(null); setError(e instanceof Error ? e.message : "Draft unavailable."); }
      }
    })();
    return () => { cancelled = true; };
  }, [draftPostId, whatsappRequestId, navigate]);

  useEffect(() => {
    if (!whatsappRequestId) return;
    let cancelled = false;
    setWhatsappReady(false);
    setWhatsappRevision(null);
    setCaptionState("");
    setItems((previous) => { previous.forEach(releasePreview); return []; });
    setNotice("Loading your WhatsApp draft…");
    void (async () => {
      try {
        // Links sent before 20260924000600 keep working: open the draft post.
        const draftPost = await whatsAppDraftPostId(whatsappRequestId);
        if (cancelled) return;
        if (draftPost) { navigate(`/compose?post=${encodeURIComponent(draftPost)}`, { replace: true }); return; }
        const draft = await readWhatsAppDraft(whatsappRequestId);
        if (cancelled) return;
        if (draft.post_id) { navigate(`/posts/${draft.post_id}`, { replace: true }); return; }
        setCaptionState(draft.caption);
        setWhatsappRevision(draft.revision);
        if (draft.has_media) {
          try {
            const file = await readWhatsAppImage(whatsappRequestId);
            const facts = await inspect(file);
            if (cancelled) return;
            const item = { key: nextKey(), file, asset: null, facts, previewUrl: URL.createObjectURL(file) };
            setItems((previous) => {
              previous.forEach(releasePreview);
              return [item];
            });
          } catch (e) {
            if (cancelled) return;
            setError(e instanceof Error ? e.message : "Upload the WhatsApp photo here.");
          }
        }
        if (cancelled) return;
        setWhatsappReady(true);
        setNotice("Review your WhatsApp photo and caption. Which brand is this for? Choose one brand, then publish or schedule using the controls below.");
      } catch (e) {
        if (!cancelled) { setNotice(null); setError(e instanceof Error ? e.message : "WhatsApp draft unavailable."); }
      }
    })();
    return () => { cancelled = true; };
  }, [whatsappRequestId, navigate]);

  // This brand's own values, falling back to the workspace's shared ones for
  // every name it does not answer itself. With no brand chosen only the shared
  // ones apply — a post being written for "all brands" must not have {{phone}}
  // silently filled in from whichever business sorted first, because the wrong
  // phone number in a published caption is not a thing that can be taken back.
  const details = useMemo(
    () => effectiveDetails(allDetails, activeBrandId),
    [allDetails, activeBrandId],
  );

  const detailValues = useMemo(
    () => new Map(details.map((d) => [d.key, d.value])),
    [details],
  );

  // Tokens left in the box that a value exists for. A token with no value is
  // not offered as fixable, because pressing the button would change nothing.
  const unresolved = useMemo(
    () => tokensIn(caption).filter((k) => detailValues.has(k)),
    [caption, detailValues],
  );

  // The first item settles the post's kind, and `create_post` refuses to mix:
  // a video goes out one per post, photos combine.
  const facts: MediaFacts | null = items[0]?.facts ?? null;
  const kind: PostKind | null = facts
    ? facts.mimeType.startsWith("video/") ? "video" : "photo"
    : null;
  const isVideo = kind === "video";
  // Slots left for the PICKER, which is a different question from whether the
  // buttons are live. A video post is full at one; a photo post takes ten.
  const slotsLeft = items.length === 0 ? MAX_PHOTOS : isVideo ? 0 : MAX_PHOTOS - items.length;
  // A full photo post is full and says so. A video post is not full, it is
  // exclusive — picking again replaces it.
  const canAddMore = isVideo || items.length < MAX_PHOTOS;

  const activeConnections = useMemo(
    () => connections.filter((c) => c.status === "active")
      // YouTube takes video only — hide it rather than let the user pick an
      // impossible target and read an error about it.
      .filter((c) => c.platform !== "youtube" || kind !== "photo"),
    [connections, kind],
  );

  // A target can disappear when the media settles the post kind (YouTube is
  // video-only) or when the workspace changes. Do not keep that now-hidden id
  // in the payload that createPost receives.
  useEffect(() => {
    const visible = new Set(activeConnections.map((connection) => connection.id));
    setSelected((current) => {
      const next = new Set([...current].filter((id) => visible.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [activeConnections]);

  useEffect(() => {
    if (!workspaceId) return;
    // A brand with no details is the ordinary case and not a failure: every
    // token simply stays as written.
    listBrandDetails(workspaceId).then(setAllDetails).catch(() => setAllDetails([]));
  }, [workspaceId]);

  // Plan-derived state. Every one of these is a hint.
  const canVideo = can(ent, "video_publishing");
  const canSchedule = can(ent, "scheduling");
  const horizon = schedulingHorizon(ent);
  const postsLeft = remaining(ent, "posts_per_month");
  const videoBlocked = kind === "video" && !canVideo;
  const mayUpload = canDo("media.upload");
  const mayPublish = canDo("post.publish");
  const maySchedule = canDo("post.schedule");
  const blockedByRole = scheduledAt ? !maySchedule : !mayPublish;

  /**
   * `datetime-local` needs a wall clock, not an ISO instant — and the wall
   * clock it needs is the one in the user's chosen zone, because that is the
   * zone the field's own value is read back in.
   */
  const horizonInputMax = useMemo(() => {
    if (!horizon) return undefined;
    return instantToWallClock(horizon, zone);
    // `horizon` is a fresh Date each render, so depend on the number it carries.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [horizon?.getTime(), zone]);

  // Only a blob: URL is ours to revoke. On unmount only — every path that drops
  // an item revokes that item's URL as it goes, and a cleanup keyed on `items`
  // would revoke the URL of a photo that is still on screen every time a
  // sibling was added.
  // `replaceCaption` is redefined on every keystroke (it closes over the
  // caption it would be replacing), and the draft hand-off must not re-run
  // because of that. The ref is the indirection that keeps the effect keyed on
  // the arrival alone.
  const replaceCaptionRef = useRef<(text: string, from: "template" | "draft") => void>(() => {});

  const live = useRef<ComposeItem[]>([]);
  live.current = items;
  useEffect(() => () => { live.current.forEach(releasePreview); }, []);

  /**
   * Add files to the post, refusing what cannot go in it.
   *
   * Three rules, all of them the database's, restated here so somebody learns
   * about them while choosing rather than after writing a caption: ten photos
   * at most, a video on its own, and never the two mixed. Refusing silently
   * would be the one unacceptable option.
   */
  const addItems = useCallback((incoming: ComposeItem[]) => {
    if (incoming.length === 0) return;
    setError(null);
    setNotice(null);
    setResults(new Map());
    setItemResults(new Map());

    const isVid = (it: ComposeItem) => it.facts.mimeType.startsWith("video/");
    const drop = (list: ComposeItem[]) => list.forEach(releasePreview);

    // Reads `items` from scope rather than through a state updater: the
    // decisions below also set a notice, and a `setItems(prev => …)` that calls
    // `setNotice` runs that during React's render phase. Every caller is an
    // event handler, so the closure is current.
    if (incoming.some(isVid)) {
      // A video is a post by itself. Whether it arrived into an empty screen,
      // on top of six photos, or alongside three more files, the answer is the
      // same one and the reason is worth saying out loud.
      const video = incoming.find(isVid)!;
      const discarded = [...items, ...incoming.filter((it) => it !== video)];
      drop(discarded);
      setItems([video]);
      if (discarded.length > 0) {
        setNotice(
          "A video goes out on its own, so it replaced everything else. Photos combine into one post; videos do not.",
        );
      }
      return;
    }

    // Photos arriving on top of a video: the same rule from the other side.
    if (items.length > 0 && isVid(items[0])) {
      const kept = incoming.slice(0, MAX_PHOTOS);
      drop([...items, ...incoming.slice(MAX_PHOTOS)]);
      setItems(kept);
      setNotice("Photos and video cannot share a post, so the video was replaced.");
      return;
    }

    const room = MAX_PHOTOS - items.length;
    const kept = incoming.slice(0, Math.max(room, 0));
    const over = incoming.length - kept.length;
    drop(incoming.slice(kept.length));
    setItems([...items, ...kept]);

    if (over > 0) {
      setNotice(
        room <= 0
          ? `This post already has ${MAX_PHOTOS} photos, which is Instagram's limit. Remove one to add another.`
          : `A post holds ${MAX_PHOTOS} photos, so the last ${over} ` +
            `${over === 1 ? "photo was" : "photos were"} left out.`,
      );
    }
  }, [items]);

  const addFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    addItems(await Promise.all(files.map(async (f) => ({
      key: nextKey(),
      file: f,
      asset: null,
      facts: await inspect(f),
      previewUrl: URL.createObjectURL(f),
    }))));
  }, [addItems]);

  /**
   * Use something already uploaded. No bytes are read: the facts come from the
   * row, which is what the validator wants anyway.
   */
  const useAssets = useCallback((assets: MediaAsset[], urls: Map<string, string>) => {
    addItems(assets.map((a) => ({
      key: nextKey(),
      file: null,
      asset: a,
      facts: {
        mimeType: a.mime_type,
        byteSize: a.byte_size,
        width: a.width,
        height: a.height,
        durationSeconds: a.duration_seconds,
      },
      previewUrl: urls.get(a.id) ?? null,
    })));
  }, [addItems]);

  const removeItem = useCallback((key: string) => {
    setItems((current) => {
      current.filter((it) => it.key === key).forEach(releasePreview);
      return current.filter((it) => it.key !== key);
    });
    setResults(new Map());
    setItemResults(new Map());
    setNotice(null);
  }, []);

  /** Carousel order is the post, so moving a slide is an edit, not a preference. */
  const moveItem = useCallback((key: string, by: -1 | 1) => {
    setItems((current) => {
      const at = current.findIndex((it) => it.key === key);
      const to = at + by;
      if (at < 0 || to < 0 || to >= current.length) return current;
      const next = [...current];
      [next[at], next[to]] = [next[to], next[at]];
      return next;
    });
  }, []);

  const toggleTarget = useCallback((connectionId: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(connectionId)) next.delete(connectionId);
      else next.add(connectionId);
      return next;
    });
  }, []);

  /** A whole brand at once, in a stated direction rather than a flip — three
   *  accounts where two are already ticked has no sensible "toggle". */
  const setTargets = useCallback((connectionIds: string[], on: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      for (const id of connectionIds) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);

  // Arriving from the favourites rail: /compose?to=<connection id> opens with
  // that account already ticked, which is the only thing "switch to this
  // account" can mean in a product that publishes rather than reads.
  //
  // Applied once per value, not once per render. `activeConnections` is rebuilt
  // whenever the post's kind changes, and without the guard adding a photo
  // would silently re-tick an account the user had just turned off.
  const appliedTo = useRef<string | null>(null);
  useEffect(() => {
    const id = params.get("to");
    if (!id || appliedTo.current === id) return;
    if (!activeConnections.some((c) => c.id === id)) return;
    appliedTo.current = id;
    setSelected((current) => (current.has(id) ? current : new Set([...current, id])));
    // Left in the URL on purpose: the rail reads it back to show which account
    // is being composed to, and it is harmless on a refresh.
  }, [params, activeConnections]);

  // Composing while a brand is being viewed opens with that brand's accounts
  // ticked. "Post as labtest1" is the action being taken, and ticking three
  // chips by hand every time is where sabu's Page gets picked up by accident.
  //
  // Three guards, and each one is a way this could go wrong rather than a
  // precaution in the abstract:
  //
  //   * once per brand id, for the same reason `?to=` is — `activeConnections`
  //     is rebuilt whenever the post's kind changes, and re-ticking an account
  //     somebody had just turned off is worse than never ticking it;
  //   * replace the previous selection when the brand changes, so accounts
  //     hidden by the new brand filter can never remain publish targets;
  //   * never alongside `?to=`, because "post to this account" is a narrower
  //     instruction than "post as this brand" and the narrower one wins.
  const appliedBrand = useRef<string | null>(null);
  useEffect(() => {
    if (params.get("to")) return;
    if (!activeBrandId || appliedBrand.current === activeBrandId) return;
    const ids = activeConnections.filter((c) => c.brand_id === activeBrandId).map((c) => c.id);
    appliedBrand.current = activeBrandId;
    setSelected(new Set(ids));
  }, [params, activeBrandId, activeConnections]);

  // Arriving from a draft on the dashboard. The caption travels in router state
  // rather than in the URL: it is long, it is the customer's words, and a query
  // string is the wrong place for both. Read once and cleared, so a refresh
  // does not paste it back over what has been written since.
  useEffect(() => {
    const draft = (location.state as { draft?: unknown } | null)?.draft;
    if (typeof draft !== "string" || draft === "") return;
    replaceCaptionRef.current(draft, "draft");
    navigate(location.pathname + location.search, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Arriving from the media library: /compose?media=<id> opens with that asset
  // already chosen, which is what "use this in a post" has to mean.
  useEffect(() => {
    const id = params.get("media");
    if (!id) return;
    (async () => {
      const a = await getMedia(id);
      // The derivative and only the derivative: the composer draws a strip of
      // small squares, and the bytes that actually go to a platform are signed
      // server-side at publish time from the original (0017-v2 §2). An asset
      // with no derivative is opened with no preview rather than by pulling the
      // original into a 90-pixel square.
      const target = thumbTarget(a);
      const key = thumbPath(a);
      const urls = target === null
        ? new Map<string, string>()
        : await signMedia([target], { surface: "compose" });
      const url = key === null ? undefined : urls.get(key);
      useAssets([a], url ? new Map([[a.id, url]]) : new Map());
    })()
      .catch((e) => setError(e.message ?? String(e)))
      .finally(() => {
        // Drop the parameter so a refresh does not re-select it over the user's
        // later choice.
        params.delete("media");
        setParams(params, { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Arriving from the calendar: /compose?at=YYYY-MM-DDTHH:mm opens with that
  // moment already in the schedule field, which is the whole point of a
  // clickable empty day. It is local wall-clock text, the same dialect the
  // `datetime-local` input speaks, so nothing is converted on the way in.
  //
  // Waits for the plan, because the three ways this can fail are all worth a
  // sentence: no scheduling on the plan, past the plan's horizon, or a time
  // that has since gone by. Silently dropping the time somebody picked is the
  // one behaviour that would make the calendar untrustworthy.
  useEffect(() => {
    if (!entSettled) return;
    const raw = params.get("at");
    if (!raw) return;

    params.delete("at");
    setParams(params, { replace: true });

    // Wall-clock text from a calendar cell that was itself drawn in this
    // zone, so it is read back in the same one rather than the browser's.
    let when: Date;
    try {
      when = wallClockToInstant(raw, zone);
    } catch {
      return;
    }
    if (Number.isNaN(when.getTime())) return;

    if (!canSchedule) {
      setNotice(
        "Your plan publishes immediately, so the time you picked on the calendar could not " +
        "be applied. This post goes out as soon as you publish it.",
      );
      return;
    }
    if (when.getTime() <= Date.now()) {
      setNotice("That time has already passed, so it was not applied. Pick a new one below.");
      return;
    }
    if (horizon && when > horizon) {
      setNotice(
        `Your plan schedules up to ${formatInZone(horizon, zone, { dateStyle: "medium" })}, ` +
        `so the time you picked on the calendar could not be applied.`,
      );
      return;
    }
    setScheduledAt(raw);
    // Runs once the plan is known; `params` changes as it is cleaned up.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entSettled]);

  const selectedPlatforms = useMemo(() => {
    const set = new Set<Platform>();
    for (const c of activeConnections) if (selected.has(c.id)) set.add(c.platform);
    return [...set];
  }, [selected, activeConnections]);

  // Validation runs the moment media and targets are both known, so the user
  // learns about a rejection before scheduling rather than after (PRD §12).
  const revalidate = useCallback(async () => {
    if (items.length === 0 || !kind || selectedPlatforms.length === 0) {
      setResults(new Map());
      setItemResults(new Map());
      return;
    }
    setChecking(true);
    try {
      // One request for the whole post, not one per photo: this runs on every
      // tick of an account chip, and ten round trips a tick would make the
      // screen feel broken on a carousel.
      const perItem = await validateMediaItems(
        items.map((it) => ({ id: it.key, media: it.facts, kind })),
        selectedPlatforms,
      );
      setItemResults(perItem);
      setResults(mergeVerdicts(
        items.map((it) => perItem.get(it.key) ?? []),
        selectedPlatforms,
        items.length > 1,
      ));
    } catch (e: any) {
      setError(e.message);
      void logError("media_validate", e, {
        step: "validate_media",
        workspaceId,
        context: { platforms: selectedPlatforms, kind, media_count: items.length,
                   mime_type: items[0].facts.mimeType, byte_size: items[0].facts.byteSize },
      });
    } finally {
      setChecking(false);
    }
    // `items` is the identity of the array, which changes on every add, remove
    // and reorder — each of which is a reason to re-check.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, kind, selectedPlatforms]);

  useEffect(() => { void revalidate(); }, [revalidate]);

  /**
   * Convert every photo this platform refused, not only the first.
   *
   * The verdict shown above the button is the whole post's, so the button has
   * to act on the whole post: fixing slide one and leaving slides three and six
   * rejected would clear nothing and look like the fix did not work.
   */
  const applyFix = useCallback(async (platform: string, _fix: string) => {
    if (items.length === 0) return;
    if (!results.get(platform)) return;

    const failing = items.filter((it) =>
      !(itemResults.get(it.key) ?? []).find((r) => r.platform === platform)?.ok);

    // Converting rewrites pixels, which needs the bytes in this tab. A library
    // asset has none here, and re-downloading a video to crop it is not a trade
    // worth making silently.
    const stored = failing.filter((it) => !it.file);
    if (stored.length === failing.length) {
      setError(
        failing.length === 1
          ? "This file is already in your library, so it cannot be converted here. " +
            "Upload a new version from your device, or publish it to the networks that accept it."
          : `${stored.length} of these photos are already in your library, so they cannot be ` +
            "converted here. Upload new versions from your device, or publish to the networks " +
            "that accept them.",
      );
      return;
    }

    setError(null);

    try {
      // Constraints come from the failing rules themselves, so the conversion
      // targets exactly what this platform asked for.
      const options = {
        maxWidth: platform === "instagram" ? 1440 : undefined,
        minWidth: platform === "instagram" ? 320 : undefined,
        maxBytes: platform === "instagram" ? 8 * 1024 * 1024 : 10 * 1024 * 1024,
        minAspect: platform === "instagram" ? 0.8 : undefined,
        maxAspect: platform === "instagram" ? 1.91 : undefined,
      };

      const converted = new Map<string, ComposeItem>();
      for (const it of failing) {
        if (!it.file) continue;
        const out = await convertImage(it.file, options);
        releasePreview(it);
        converted.set(it.key, {
          ...it,
          file: out,
          facts: await inspect(out),
          previewUrl: URL.createObjectURL(out),
        });
      }

      setItems((current) => current.map((it) => converted.get(it.key) ?? it));

      const one = converted.size === 1 ? [...converted.values()][0] : null;
      setNotice(
        (one
          ? `Converted to JPEG at ${one.facts.width}×${one.facts.height}, ${formatBytes(one.file!.size)}. `
          : `Converted ${converted.size} photos to JPEG. `) +
        (stored.length > 0
          ? `${stored.length} could not be converted here because ${stored.length === 1 ? "it is" : "they are"} already in your library. `
          : "") +
        "Check the previews — an image may have been cropped to fit.",
      );
    } catch (e: any) {
      setError(e.message);
      void logError("media_convert", e, {
        step: "convert_image",
        workspaceId,
        platform,
        context: { media_count: items.length, mime_type: items[0].facts.mimeType,
                   byte_size: items[0].facts.byteSize,
                   width: items[0].facts.width, height: items[0].facts.height },
      });
    }
  }, [items, results, itemResults, workspaceId]);

  /**
   * The two controls that can overwrite something somebody typed — a template
   * and a saved draft — go through here, so both offer the same way back.
   *
   * The offer lasts only until the next keystroke: once they have started
   * working on what landed in the box, restoring is itself the destructive act.
   */
  const replaceCaption = useCallback((text: string, from: "template" | "draft") => {
    setCaptionUndo(caption.trim() ? { text: caption, from } : null);
    // Resolution happens here and nowhere else. The library stores
    // `{{booking}}` for as long as the draft exists, so changing the link
    // changes every future post and none of the published ones; this is the
    // moment "every future post" becomes this one.
    const { text: filled, missing } = resolveTokens(text, detailValues);
    setCaptionState(filled);
    setNotice(
      missing.length === 0 ? null
        : `This ${from === "draft" ? "caption" : "template"} mentions ` +
          `${missing.map((k) => `{{${k}}}`).join(", ")}, which ${active?.name ?? "this brand"} ` +
          `has no value for. Add it on the caption library, or write over it here.`,
    );
  }, [caption, detailValues, active?.name]);

  replaceCaptionRef.current = replaceCaption;

  /**
   * A template is a starting point, not a selection: it drops text in and the
   * dropdown goes straight back to its placeholder, because there is nothing
   * afterwards that is still "the template you chose".
   */
  const applyTemplate = useCallback((key: string) => {
    const template = CAPTION_TEMPLATES.find((t) => t.key === key);
    if (!template) return;
    replaceCaption(template.body, "template");
  }, [replaceCaption]);

  const setCaption = useCallback((text: string) => {
    setCaptionState(text);
    setCaptionUndo(null);
  }, []);

  const fillTokens = useCallback(() => {
    setCaptionState((current) => resolveTokens(current, detailValues).text);
  }, [detailValues]);

  const reset = useCallback(() => {
    live.current.forEach(releasePreview);
    setItems([]);
    setSelected(new Set());
    setResults(new Map());
    setItemResults(new Map());
    setCaptionState("");
    setCaptionUndo(null);
    setTitle("");
    setScheduledAt("");
    setProgress(0);
    setUploaded(0);
    setStage("idle");
  }, []);

  /**
   * Keep the caption in the library, without publishing anything.
   *
   * A draft here is a draft everywhere: the same `content_drafts` rows the
   * studio writes and the caption library lists, so what is saved on the
   * dashboard is offered back by "Use a saved draft" on the full screen. No
   * media travels with it — the library is words.
   */
  const saveAsDraft = useCallback(async () => {
    if (!workspaceId || !caption.trim()) return;
    setSavingDraft(true);
    setError(null);
    try {
      await saveDraft({
        workspaceId,
        kind: "caption",
        body: caption,
        source: "manual",
        mediaAssetId: items.find((it) => it.asset)?.asset?.id ?? null,
      });
      setNotice("Saved to your caption library.");
    } catch (e: any) {
      if (isPaywall(e)) {
        setPaywall(paywallMessage(e));
      } else {
        setError(e.message ?? String(e));
        void logError("app", e, { step: "save_draft", workspaceId });
      }
    } finally {
      setSavingDraft(false);
    }
  }, [workspaceId, caption, items]);

  const allReady = selectedPlatforms.length > 0 &&
    selectedPlatforms.every((p) => results.get(p)?.ok);

  const publish = useCallback(async () => {
    if (items.length === 0 || !facts || !kind || !workspaceId) return;
    if (whatsappFlow && (!whatsappReady || (whatsappRequestId && whatsappRevision === null) || !activeBrandId || kind !== "photo")) {
      setError("Review the WhatsApp draft and choose one brand and a photo before publishing.");
      return;
    }
    setError(null);
    setNotice(null);
    setPaywall(null);

    // One id for the whole publish, shared with the YouTube upload and with
    // the Edge Function, so every row from this attempt can be read together.
    const runId = newRunId();
    let postId: string | null = null;
    let currentStage: Stage = "uploading-media";

    try {
      // Already in storage? Then there is nothing to upload — the post just
      // points at the row that is already there. In order, and one at a time:
      // the order is the carousel, and ten parallel uploads from a phone on
      // hotel wifi is how you get ten failures instead of one.
      currentStage = "uploading-media";
      const mediaAssetIds: string[] = [];
      for (const it of items) {
        if (it.asset) {
          mediaAssetIds.push(it.asset.id);
          continue;
        }
        setStage("uploading-media");
        setUploaded(mediaAssetIds.length);
        mediaAssetIds.push(await uploadMedia(workspaceId, it.file!, it.facts, null, whatsappFlow ? activeBrandId : null));
      }

      currentStage = "creating";
      setStage("creating");
      const post = await createPost({
        whatsapp: whatsappRequestId && activeBrandId && whatsappRevision !== null
          ? { requestId: whatsappRequestId, revision: whatsappRevision, brandId: activeBrandId } : undefined,
        draftPostId: !whatsappRequestId && draftPostId ? draftPostId : undefined,
        workspaceId,
        kind,
        mediaAssetIds,
        caption,
        title: title || null,
        connectionIds: [...selected],
        // The one line that turns "09:00" into a moment. `scheduledAt` is a
        // wall clock and means nothing until a zone is named; naming the
        // user's rather than the browser's is the whole point of ADR-0031.
        scheduledAt: scheduledAt ? wallClockToInstant(scheduledAt, zone).toISOString() : null,
        youtubePrivacyStatus: ytPrivacy,
      });
      postId = post.id;
      // The month's quota just moved, and the sidebar draws it.
      forgetEntitlements(workspaceId);

      if (scheduledAt) {
        setStage("done");
        navigate(`/posts/${post.id}`);
        return;
      }

      // YouTube needs the bytes from this tab (ADR-0004). Do it while the
      // file is still in memory — a scheduled post falls back to the
      // server-side streaming path instead.
      //
      // A library asset has no bytes here. Re-downloading a multi-gigabyte
      // video into the browser only to send it straight back out is the exact
      // round trip ADR-0004 exists to avoid, so those go the server route.
      // Video is always one item (`create_post` refuses to mix), so "the file"
      // is unambiguous here even though the post now holds a list.
      const file = items[0].file;
      const ytDeliveries = file ? await youtubeDeliveriesFor(post.id) : [];
      if (ytDeliveries.length > 0) {
        currentStage = "uploading-youtube";
        setStage("uploading-youtube");
        for (const d of ytDeliveries) {
          const r = await uploadToYouTube(d.id, file!, (p) => setProgress(p.percent),
                                          { postId: post.id, workspaceId, runId });
          // YouTube can apply something narrower than we asked for. Say so
          // rather than letting the difference look like a bug.
          if (r.privacyStatus !== r.requestedPrivacyStatus) {
            setNotice(
              `You chose ${r.requestedPrivacyStatus}, but YouTube published this video as ` +
              `${r.privacyStatus}. You can change it in YouTube Studio.`,
            );
          }
        }
      }

      // Nudge the executor for the remaining networks rather than waiting up
      // to a minute for the next cron tick.
      currentStage = "dispatching";
      setStage("dispatching");
      await callFunction("publish-executor", { post_id: post.id }).catch((e) =>
        logError("publish_dispatch", e, {
          step: "publish_executor", level: "warn", runId, workspaceId, postId: post.id,
          // Not fatal: the scheduler picks the deliveries up on its next tick.
          context: { recovered_by: "scheduler_tick" },
        }));

      setStage("done");
      navigate(`/posts/${post.id}`);
    } catch (e: any) {
      if (isPaywall(e)) {
        // Not a failure — a plan boundary. Said plainly, with the way out.
        setPaywall(paywallMessage(e));
        setStage("idle");
        void logError("paywall", e, {
          level: "info",
          step: e.detail?.feature ?? "unknown",
          workspaceId,
          runId,
          context: { ...(e.detail ?? {}), stage: currentStage },
        });
        return;
      }
      setError(e.message ?? String(e));
      setStage("idle");
      // The stage is the step: "which part of publishing broke" is the first
      // question asked about every one of these.
      void logError(
        currentStage === "uploading-youtube" ? "youtube_upload"
          : currentStage === "creating" ? "post_create"
          : currentStage === "dispatching" ? "publish_dispatch"
          : "media_upload",
        e,
        {
          step: currentStage,
          runId,
          workspaceId,
          postId,
          context: {
            stage: currentStage,
            platforms: selectedPlatforms,
            kind,
            media_count: items.length,
            byte_size: items.reduce((n, it) => n + it.facts.byteSize, 0),
            mime_type: facts.mimeType,
            scheduled: !!scheduledAt,
            progress_percent: progress,
          },
        },
      );
    }
  }, [items, facts, kind, workspaceId, caption, title, selected, scheduledAt, ytPrivacy,
      selectedPlatforms, progress, navigate, zone, whatsappRequestId, whatsappReady, whatsappRevision, activeBrandId, whatsappFlow, draftPostId]);

  const busy = stage !== "idle" && stage !== "done";

  return {
    whatsappRequestId, whatsappFlow, whatsappReady, items, caption, title, scheduledAt, ytPrivacy, selected,
    workspaceId, facts, kind, isVideo, slotsLeft, canAddMore, activeConnections,
    brands, activeBrandId, selectBrand,
    selectedPlatforms, results, checking, allReady, unresolved, detailValues, details,
    canVideo, canSchedule, horizon, horizonInputMax, postsLeft, videoBlocked, mayUpload,
    zone, zoneName: zoneName(zone),
    blockedByRole, entSettled,
    stage, busy, progress, uploaded, error, notice, paywall, savingDraft,
    setCaption, setTitle, setScheduledAt, setYtPrivacy, setError, setNotice,
    addFiles, useAssets, removeItem, moveItem, toggleTarget, setTargets, applyFix, applyTemplate,
    replaceCaption, fillTokens, captionUndo, undoCaption: () => {
      if (captionUndo) { setCaptionState(captionUndo.text); setCaptionUndo(null); }
    },
    saveAsDraft, publish, reset,
  };
}
